import { MAX_PERIOD_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import { MODE } from '../PagesQueriesChanges/scope';
import {
    MAX_GROUPS,
    MAX_NAME_LENGTH,
    MAX_RULES_PER_GROUP,
    MAX_VALUE_LENGTH,
    OPERATORS,
    groupsFrom,
    ruleError,
    ruleLabel,
} from './groups';

/**
 * How block 02 orders the groups: the same three orders, with the same meaning, as the rows of
 * Pages, Queries & Changes. Popular ranks the current value of the selected metric; Growing and
 * Declining keep only groups with a comparable value in both windows and rank how far it moved.
 */
export const MODES = [MODE.POPULAR, MODE.GAINING, MODE.DECLINING];

/** The metrics the order and the trend open in. */
export const ORDER_METRICS = ['clicks', 'impressions'];

/**
 * The scope this report runs under: the shared one, plus the session's group rules, the group the
 * detail blocks are about, and how the comparison is ordered.
 *
 * The rules are part of the scope rather than a view over fetched rows, because they are in the
 * request: every figure on the page is a page-filtered ask of Search Console, so changing a rule
 * changes what was fetched and not just what is drawn. That is why they sit behind the same Apply
 * as the dates.
 *
 * The window is the shared headline bound. Only the matching-URL block carries a page dimension,
 * and it shortens its own window through the shared detail helper and says so, rather than holding
 * the whole report to the shorter bound for the sake of one block.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        // Session-only, in the order they were added. Nothing is stored in the account or in the
        // browser; the rules travel with the applied scope in the page's address, which is the
        // whole of their lifetime.
        rules: [],
        group: '',
        mode: MODE.POPULAR,
        metric: 'clicks',
    };
}

/**
 * The draft as a scope, with every reason it cannot be applied.
 *
 * A session with no groups is refused rather than answered: with nothing to filter by, the report
 * would quietly become a whole-property fetch, and figures under a "content group" heading that
 * are really the property's own would be read as a group that covers everything.
 */
export function resolveReportScope(draft, t) {
    const scope = resolveScope(draft, t, { maxDays: MAX_PERIOD_DAYS });

    const rules = [];
    for (const rule of draft.rules ?? []) {
        const error = ruleError(rule, t);
        if (error) scope.errors.push(`${ruleLabel(t, rule)} — ${error}`);
        else rules.push(rule);
    }

    const groups = groupsFrom(rules);

    if (!groups.length) {
        scope.errors.push(t('tools.content_groups.errors.no_groups'));
    } else if (groups.length > MAX_GROUPS) {
        scope.errors.push(t('tools.content_groups.errors.too_many_groups', { max: MAX_GROUPS }));
    }

    for (const group of groups) {
        if (group.rules.length > MAX_RULES_PER_GROUP) {
            scope.errors.push(
                t('tools.content_groups.errors.too_many_rules', {
                    group: group.name,
                    max: MAX_RULES_PER_GROUP,
                }),
            );
        }
    }

    const active = groups.find((group) => group.name === draft.group) ?? groups[0] ?? null;

    return {
        ...scope,
        rules,
        groups,
        // The group blocks 03 and 04 are about. Null once the user has cleared it, which leaves
        // the comparison standing and the detail blocks waiting for a pick.
        group: active?.name ?? null,
        mode: MODES.includes(draft.mode) ? draft.mode : MODE.POPULAR,
        metric: ORDER_METRICS.includes(draft.metric) ? draft.metric : 'clicks',
    };
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['rules', 'group', 'mode', 'metric'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own controls too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

/**
 * One rule as the address can carry it, or null.
 *
 * A query string is typed by hand as easily as it is copied, so a rule is taken only when its
 * operator is one this report offers and its name and value are text within the bounds a typed
 * one may have. Whether an expression reads is left to `ruleError`, which reports it as a scope
 * error the user can see rather than dropping the rule silently here.
 */
function readRule(rule) {
    if (!rule || typeof rule !== 'object') return null;
    if (!OPERATORS.includes(rule.operator)) return null;
    if (typeof rule.group !== 'string' || !rule.group.trim()) return null;
    if (rule.group.length > MAX_NAME_LENGTH) return null;
    if (typeof rule.value !== 'string' || !rule.value.trim()) return null;
    if (rule.value.length > MAX_VALUE_LENGTH) return null;

    return { group: rule.group.trim(), operator: rule.operator, value: rule.value.trim() };
}

/** The draft this report starts from when its address already describes one. */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (params.group) draft.group = params.group;
    if (MODES.includes(params.mode)) draft.mode = params.mode;
    if (ORDER_METRICS.includes(params.metric)) draft.metric = params.metric;

    if (params.rules) {
        try {
            const parsed = JSON.parse(params.rules);
            const rules = (Array.isArray(parsed) ? parsed : []).map(readRule).filter(Boolean);
            if (rules.length) draft.rules = rules;
        } catch (err) {
            // A rules parameter that is not JSON is not a report anyone meant to open; the page
            // starts with no groups rather than refusing to open at all.
            console.log(err);
        }
    }

    return draft;
}

/** The applied scope as query parameters. */
export function reportParamsFromScope(scope) {
    return {
        ...scopeParamsFromScope(scope),
        rules: scope.rules?.length ? JSON.stringify(scope.rules) : undefined,
        group: scope.group ?? undefined,
        mode: scope.mode,
        metric: scope.metric,
    };
}
